/**
 * Utility functions for locking device orientation to portrait mode
 */

import { isMobileDevice, supportsOrientationChange } from './device-detection';

export const lockOrientation = async (): Promise<void> => {
  // Only attempt to lock orientation on mobile devices
  if (!isMobileDevice() || !supportsOrientationChange()) {
    return;
  }

  try {
    // Modern Screen Orientation API
    if ('screen' in window && 'orientation' in window.screen && 'lock' in window.screen.orientation) {
      await (window.screen.orientation as any).lock('portrait');
      return;
    }

    // Legacy Screen Orientation API
    const screen = window.screen as any;
    if (typeof screen.lockOrientation === 'function') {
      screen.lockOrientation('portrait');
      return;
    }
    
    if (typeof screen.mozLockOrientation === 'function') {
      screen.mozLockOrientation('portrait');
      return;
    }
    
    if (typeof screen.msLockOrientation === 'function') {
      screen.msLockOrientation('portrait');
      return;
    }

    // Webkit orientation lock
    if (typeof screen.webkitLockOrientation === 'function') {
      screen.webkitLockOrientation('portrait');
      return;
    }

    console.warn('Orientation lock not supported on this device/browser');
  } catch (error) {
    console.warn('Failed to lock orientation:', error);
  }
};

export const unlockOrientation = (): void => {
  // Only attempt to unlock orientation on mobile devices
  if (!isMobileDevice() || !supportsOrientationChange()) {
    return;
  }

  try {
    // Modern Screen Orientation API
    if ('screen' in window && 'orientation' in window.screen && 'unlock' in window.screen.orientation) {
      (window.screen.orientation as any).unlock();
      return;
    }

    // Legacy Screen Orientation API
    const screen = window.screen as any;
    if (typeof screen.unlockOrientation === 'function') {
      screen.unlockOrientation();
      return;
    }
    
    if (typeof screen.mozUnlockOrientation === 'function') {
      screen.mozUnlockOrientation();
      return;
    }
    
    if (typeof screen.msUnlockOrientation === 'function') {
      screen.msUnlockOrientation();
      return;
    }

    if (typeof screen.webkitUnlockOrientation === 'function') {
      screen.webkitUnlockOrientation();
      return;
    }
  } catch (error) {
    console.warn('Failed to unlock orientation:', error);
  }
};

export const isPortrait = (): boolean => {
  if ('screen' in window && 'orientation' in window.screen && 'angle' in window.screen.orientation) {
    const angle = (window.screen.orientation as any).angle;
    return angle === 0 || angle === 180;
  }
  
  // Fallback to window dimensions
  return window.innerHeight > window.innerWidth;
};

export const addOrientationChangeListener = (callback: (isPortrait: boolean) => void): (() => void) => {
  // Only add orientation listeners on mobile devices
  if (!isMobileDevice() || !supportsOrientationChange()) {
    // Return a no-op cleanup function for non-mobile devices
    return () => {};
  }

  const handleOrientationChange = () => {
    callback(isPortrait());
  };

  // Modern API
  if ('screen' in window && 'orientation' in window.screen && 'addEventListener' in window.screen.orientation) {
    (window.screen.orientation as any).addEventListener('change', handleOrientationChange);
    return () => (window.screen.orientation as any).removeEventListener('change', handleOrientationChange);
  }

  // Legacy API
  window.addEventListener('orientationchange', handleOrientationChange);
  return () => window.removeEventListener('orientationchange', handleOrientationChange);
};
